import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminServicesPage = () => {
  const { getAuthHeader } = useAdmin();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    icon: '',
    title: '',
    description: '',
    timeline: '',
    outcomes: '',
    included: '',
    addons: '',
    order: 0,
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (service) => {
    setEditingId(service.id);
    setFormData({
      icon: service.icon,
      title: service.title,
      description: service.description,
      timeline: service.timeline,
      outcomes: service.outcomes.join('\n'),
      included: service.included.join('\n'),
      addons: service.addons.join('\n'),
      order: service.order,
    });
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({
      icon: '',
      title: '',
      description: '',
      timeline: '',
      outcomes: '',
      included: '',
      addons: '',
      order: services.length,
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setFormData({
      icon: '',
      title: '',
      description: '',
      timeline: '',
      outcomes: '',
      included: '',
      addons: '',
      order: 0,
    });
  };

  const handleSave = async () => {
    try {
      const data = {
        icon: formData.icon,
        title: formData.title,
        description: formData.description,
        timeline: formData.timeline,
        outcomes: formData.outcomes.split('\n').filter(Boolean),
        included: formData.included.split('\n').filter(Boolean),
        addons: formData.addons.split('\n').filter(Boolean),
        order: formData.order,
      };

      if (isAdding) {
        await axios.post(`${API}/services`, data, { headers: getAuthHeader() });
        toast.success('Service added successfully');
      } else {
        await axios.put(`${API}/services/${editingId}`, data, { headers: getAuthHeader() });
        toast.success('Service updated successfully');
      }

      fetchServices();
      handleCancel();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;

    try {
      await axios.delete(`${API}/services/${id}`, { headers: getAuthHeader() });
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-services-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" size="icon" className="rounded-full">
                  <ArrowLeft size={18} />
                </Button>
              </Link>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-900">Manage Services</h1>
                <p className="text-slate-600">Add, edit, or remove service offerings</p>
              </div>
            </div>
            {!isAdding && !editingId && (
              <Button onClick={handleAdd} className="bg-[#10B981] hover:bg-[#059669] rounded-full" data-testid="add-service-button">
                <Plus size={18} className="mr-2" />
                Add Service
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Add/Edit Form */}
        {(isAdding || editingId) && (
          <div className="bg-white rounded-2xl p-8 border border-slate-200 mb-8" data-testid="service-form">
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
              {isAdding ? 'Add New Service' : 'Edit Service'}
            </h2>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="icon">Icon Name (Lucide React)</Label>
                  <Input
                    id="icon"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    placeholder="e.g., Code, Palette, Layout"
                    data-testid="service-icon-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Use Lucide React icon names</p>
                </div>
                <div>
                  <Label htmlFor="timeline">Timeline</Label>
                  <Input
                    id="timeline"
                    value={formData.timeline}
                    onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                    placeholder="e.g., 5-7 days"
                    data-testid="service-timeline-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Service title"
                  data-testid="service-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Service description"
                  rows={3}
                  data-testid="service-description-input"
                />
              </div>

              <div>
                <Label htmlFor="outcomes">Outcomes (one per line)</Label>
                <Textarea
                  id="outcomes"
                  value={formData.outcomes}
                  onChange={(e) => setFormData({ ...formData, outcomes: e.target.value })}
                  placeholder="Custom design mockups\nMobile-responsive layouts"
                  rows={4}
                  data-testid="service-outcomes-input"
                />
              </div>

              <div>
                <Label htmlFor="included">What's Included (one per line)</Label>
                <Textarea
                  id="included"
                  value={formData.included}
                  onChange={(e) => setFormData({ ...formData, included: e.target.value })}
                  placeholder="2 design concepts\n2 rounds of revisions"
                  rows={4}
                  data-testid="service-included-input"
                />
              </div>

              <div>
                <Label htmlFor="addons">Add-ons (one per line)</Label>
                <Textarea
                  id="addons"
                  value={formData.addons}
                  onChange={(e) => setFormData({ ...formData, addons: e.target.value })}
                  placeholder="Additional pages\nCustom illustrations"
                  rows={3}
                  data-testid="service-addons-input"
                />
              </div>

              <div className="flex gap-4">
                <Button onClick={handleSave} className="bg-[#10B981] hover:bg-[#059669] rounded-full" data-testid="save-service-button">
                  <Save size={18} className="mr-2" />
                  Save Service
                </Button>
                <Button onClick={handleCancel} variant="outline" className="rounded-full" data-testid="cancel-service-button">
                  <X size={18} className="mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Services List */}
        <div className="space-y-4">
          {services.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
              <p className="text-slate-600 mb-4">No services yet. Add your first service!</p>
              <Button onClick={handleAdd} className="bg-[#10B981] hover:bg-[#059669] rounded-full">
                <Plus size={18} className="mr-2" />
                Add Service
              </Button>
            </div>
          ) : (
            services.map((service, index) => (
              <div
                key={service.id}
                className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-[#10B981] transition-colors"
                data-testid={`service-item-${index}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold text-slate-900 mb-2">{service.title}</h3>
                    <p className="text-slate-600 mb-4">{service.description}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="bg-slate-100 px-3 py-1 rounded-full">Timeline: {service.timeline}</span>
                      <span className="bg-slate-100 px-3 py-1 rounded-full">{service.outcomes.length} outcomes</span>
                      <span className="bg-slate-100 px-3 py-1 rounded-full">{service.included.length} included items</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleEdit(service)}
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                      data-testid={`edit-service-${index}`}
                    >
                      <Edit size={18} />
                    </Button>
                    <Button
                      onClick={() => handleDelete(service.id)}
                      variant="outline"
                      size="icon"
                      className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-service-${index}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
